// Service catalog data
import {
  Server, Cloud, Cpu, Network, Code2, Plane,
  Shield, Zap, Layers, Database, Wifi, Cog,
  Radar, Radio, Route, LineChart, GitBranch, Boxes,
  ShieldCheck, Users, Globe, Award,
} from "lucide-react";
import React from "react";

export const services = [
  {
    slug: "it-infrastructure",
    title: "IT Infrastructure & Hardware",
    tagline: "Enterprise Solutions",
    short: "Enterprise-grade servers, storage solutions, and networking equipment for mission-critical operations.",
    image: "/images/service-it.jpg",
    icon: <Server size={22} />,
    iconCls: "red",
    overview:
      "We supply, design and deploy carrier-class IT infrastructure for organizations that cannot afford downtime. From compute and storage foundations to high-availability data centers, our team engineers systems that scale with your business and remain resilient under real-world load.",
    offerings: [
      { icon: <Server size={18} />, title: "Compute & Server Platforms", body: "Rack, blade and hyper-converged servers from Tier-1 vendors, sized for your workload profile and growth roadmap." },
      { icon: <Database size={18} />, title: "Enterprise Storage", body: "All-flash, hybrid and object storage arrays with replication, snapshots and lifecycle policies built in." },
      { icon: <Layers size={18} />, title: "Data Center Design", body: "End-to-end design of primary and disaster-recovery sites, including power, cooling and rack layout." },
      { icon: <Shield size={18} />, title: "Backup & Business Continuity", body: "Immutable backups, offsite replication and tested recovery runbooks so you meet your RPO and RTO targets." },
      { icon: <Cog size={18} />, title: "Lifecycle Management", body: "Procurement, installation, patching, hardware refresh and controlled decommissioning under a single contract." },
      { icon: <Zap size={18} />, title: "24/7 Operations", body: "Monitoring, alerting and remote hands, coordinated with vendor support to shorten mean-time-to-resolve." },
    ],
    capabilities: [
      "Tier-3 and Tier-4 data center readiness assessments",
      "Rack and stack, cabling and structured wiring",
      "SAN, NAS and object storage integration",
      "Hyper-converged (HCI) reference architectures",
      "Backup, replication and DR orchestration",
      "Hardware asset lifecycle and warranty management",
    ],
    partners: ["Enterprise servers", "Storage arrays", "Backup platforms", "Data-center hardware"],
    useCases: [
      "Airport and ANSP operational data centers",
      "Government and regulator IT modernization",
      "Financial services core banking environments",
      "Telecom operator back-office and OSS/BSS platforms",
      "Oil & gas enterprise IT and industrial data centers",
    ],
  },
  {
    slug: "virtualization-cloud",
    title: "Virtualization & Cloud",
    tagline: "Future-Ready Technology",
    short: "Modern virtualization platforms and cloud infrastructure for scalable, flexible IT environments.",
    image: "/images/service-cloud.jpg",
    icon: <Cloud size={22} />,
    iconCls: "green",
    overview:
      "Whether you are consolidating a physical estate, building a private cloud, or extending workloads into public and hybrid environments, we architect virtualization and cloud platforms that deliver elasticity without compromising on control, security or compliance.",
    offerings: [
      { icon: <Cloud size={18} />, title: "Private & Hybrid Cloud", body: "Reference architectures for on-premises private clouds and controlled hybrid extensions into public providers." },
      { icon: <Boxes size={18} />, title: "Virtualization Platforms", body: "Server, storage and network virtualization design with performance baselines and capacity planning." },
      { icon: <GitBranch size={18} />, title: "Workload Migration", body: "Assessment, wave planning and low-risk migration of workloads, including replatforming where it pays off." },
      { icon: <Shield size={18} />, title: "Cloud Security", body: "Identity, segmentation, key management and posture monitoring embedded into every landing zone." },
      { icon: <LineChart size={18} />, title: "FinOps & Optimization", body: "Right-sizing, reserved capacity strategy and continuous cost visibility across environments." },
      { icon: <Cog size={18} />, title: "Automation & IaC", body: "Infrastructure as Code, GitOps pipelines and repeatable environments for dev, test and production." },
    ],
    capabilities: [
      "Landing zone and account/subscription architecture",
      "VMware, KVM and container platform design",
      "Kubernetes clusters for stateful and stateless workloads",
      "Storage virtualization and software-defined storage",
      "Disaster recovery in and to the cloud",
      "Chargeback, showback and cost governance",
    ],
    partners: ["Virtualization vendors", "Hyper-converged platforms", "Kubernetes distributions", "Public cloud providers"],
    useCases: [
      "Consolidation of legacy physical server estates",
      "Private cloud for regulated public-sector workloads",
      "Hybrid extension for burst capacity and DR",
      "Modernization of monolithic applications to containers",
      "Self-service developer platforms",
    ],
  },
  {
    slug: "ai-innovation",
    title: "AI & Innovation",
    tagline: "Intelligent Automation",
    short: "Cutting-edge AI solutions for predictive maintenance, automation, and intelligent decision-making.",
    image: "/images/service-ai.jpg",
    imagePosition: "right center",
    icon: <Cpu size={22} />,
    iconCls: "",
    overview:
      "We bring artificial intelligence out of the lab and into operational workflows. Our teams identify high-value use cases, build production-grade models and integrate them with your existing systems, so AI becomes a measurable driver of efficiency, safety and revenue rather than a proof-of-concept.",
    offerings: [
      { icon: <Cpu size={18} />, title: "AI Strategy & Discovery", body: "Structured workshops to identify use cases with clear ROI, feasibility scoring and a delivery roadmap." },
      { icon: <LineChart size={18} />, title: "Predictive Analytics", body: "Forecasting, anomaly detection and predictive maintenance models trained on your operational data." },
      { icon: <Radar size={18} />, title: "Computer Vision", body: "Object detection, tracking and classification for safety, quality control and access management." },
      { icon: <Code2 size={18} />, title: "Generative AI & LLMs", body: "Retrieval-augmented assistants, document intelligence and workflow copilots on top of enterprise data." },
      { icon: <Cog size={18} />, title: "MLOps & Deployment", body: "Model registries, monitoring and safe rollout pipelines that treat AI as a first-class production system." },
      { icon: <Shield size={18} />, title: "Responsible AI", body: "Data protection, bias evaluation and human-in-the-loop controls aligned with regional regulations." },
    ],
    capabilities: [
      "AI opportunity assessment and business case",
      "Data engineering and feature pipelines",
      "Model development in supervised and unsupervised settings",
      "Vector databases and retrieval-augmented pipelines",
      "Edge deployment for low-latency inference",
      "Model monitoring, drift detection and retraining",
    ],
    partners: ["ML platforms", "Vector databases", "GPU compute providers", "Foundation model providers"],
    useCases: [
      "Predictive maintenance for aviation and industrial assets",
      "Automated document processing for back-office teams",
      "Predictive maintenance and anomaly detection for oil & gas operations",
      "Smart video analytics for airports and public spaces",
      "Internal AI copilots on top of enterprise knowledge",
    ],
  },
  {
    slug: "network-solutions",
    title: "Network Solutions",
    tagline: "Seamless Connectivity",
    short: "Comprehensive routing, switching, and network infrastructure for seamless connectivity and data flow.",
    image: "/images/service-network.jpg",
    icon: <Network size={22} />,
    iconCls: "red",
    overview:
      "Networks are the invisible backbone of every modern operation. We design, deploy and support carrier-grade LAN, WAN, data center and wireless environments that stay predictable under load and adapt as your traffic patterns evolve.",
    offerings: [
      { icon: <Network size={18} />, title: "Campus & Data-Center LAN", body: "High-throughput, low-latency switching fabrics designed for east-west traffic and non-blocking performance." },
      { icon: <Route size={18} />, title: "WAN & SD-WAN", body: "Resilient multi-site connectivity with application-aware routing and centralized policy management." },
      { icon: <Wifi size={18} />, title: "Enterprise Wireless", body: "Site-surveyed Wi-Fi 6/6E deployments with seamless roaming and dense-client capacity planning." },
      { icon: <Shield size={18} />, title: "Network Security", body: "Segmentation, next-generation firewalls and zero-trust access aligned with your identity systems." },
      { icon: <LineChart size={18} />, title: "Observability", body: "Flow analytics, performance baselines and proactive alerting so problems surface before users notice." },
      { icon: <Cog size={18} />, title: "Automation", body: "Template-driven provisioning and change control that reduce manual toil and human error." },
    ],
    capabilities: [
      "Routing, switching and load balancing design",
      "SD-WAN and MPLS transition strategies",
      "Wi-Fi predictive and post-deployment surveys",
      "Micro-segmentation and zero-trust network access",
      "DNS, DHCP and IPAM consolidation",
      "Bandwidth planning and QoS engineering",
    ],
    partners: ["Enterprise networking vendors", "SD-WAN platforms", "Wireless controllers", "Next-generation firewalls"],
    useCases: [
      "Airport and ANSP operational networks",
      "Multi-branch enterprise WAN modernization",
      "High-density venues and campuses",
      "Data-center fabric refresh and expansion",
      "Segmented OT / IT convergence for critical facilities",
    ],
  },
  {
    slug: "software-solutions",
    title: "Software Solutions",
    tagline: "Tailored Applications",
    short: "Custom software development and enterprise applications tailored to aviation and IT sectors.",
    image: "/images/service-software.jpg",
    icon: <Code2 size={22} />,
    iconCls: "green",
    overview:
      "Off-the-shelf products rarely cover every operational nuance. We build focused custom software — from internal tools and workflow platforms to full-scale operational systems — and integrate them cleanly with the applications you already run.",
    offerings: [
      { icon: <Code2 size={18} />, title: "Custom Application Development", body: "Web, mobile and desktop applications built to fit your specific processes and compliance context." },
      { icon: <GitBranch size={18} />, title: "System Integration", body: "APIs, ETL pipelines and middleware that make heterogeneous systems behave as one coherent platform." },
      { icon: <Layers size={18} />, title: "Legacy Modernization", body: "Incremental replatforming of older systems, preserving business logic while improving maintainability." },
      { icon: <Boxes size={18} />, title: "Product Engineering", body: "End-to-end teams for organizations building their own SaaS or operational products." },
      { icon: <Shield size={18} />, title: "Secure by Design", body: "Threat modelling, secure SDLC and third-party dependency review baked into every project." },
      { icon: <Cog size={18} />, title: "DevOps & CI/CD", body: "Automated pipelines, quality gates and observability so releases are predictable and low-drama." },
    ],
    capabilities: [
      "Discovery workshops and product architecture",
      "Full-stack development in modern JS, Python and .NET stacks",
      "Domain-driven design and micro-services patterns",
      "API design with REST, GraphQL and event-driven models",
      "Quality assurance and automated test coverage",
      "Ongoing maintenance and evolutionary roadmaps",
    ],
    partners: ["Cloud application platforms", "API management vendors", "DevOps tool chains", "Application security tooling"],
    useCases: [
      "Operational dashboards for aviation and IT teams",
      "Workflow automation for regulated back-office processes",
      "Portals and self-service for partners and customers",
      "Integration layers connecting ERP, CRM and operational systems",
      "Data marketplaces and internal analytics products",
    ],
  },
  {
    slug: "air-traffic-management",
    title: "Air Traffic Management Solutions",
    tagline: "Advanced ATM Systems",
    short: "Advanced ATM systems for airports and aviation authorities, ensuring safe and efficient air traffic control operations.",
    image: "/images/service-atm.jpg",
    icon: <Plane size={22} />,
    iconCls: "",
    overview:
      "Aviation is the domain where reliability is not negotiable. We deliver systems that support air navigation service providers, airport operators and civil aviation authorities across the full lifecycle — from feasibility and design to commissioning, training and long-term support.",
    offerings: [
      { icon: <Radar size={18} />, title: "Surveillance Systems", body: "Primary, secondary and multilateration surveillance integration for terminal and en-route environments." },
      { icon: <Radio size={18} />, title: "Voice Communications", body: "Ground-to-ground and air-to-ground voice systems, including recording, replay and legal-grade retention." },
      { icon: <Route size={18} />, title: "ATC Automation", body: "Controller working positions, flight data processing and integration with adjacent ATM systems." },
      { icon: <Plane size={18} />, title: "Airport Operational Systems", body: "AODB, resource management and information display platforms for turnaround efficiency." },
      { icon: <Cog size={18} />, title: "Simulation & Training", body: "Tower and radar simulators for controller training, procedure validation and safety-case rehearsal." },
      { icon: <ShieldCheck size={18} />, title: "Safety & Compliance", body: "Deliveries aligned with ICAO, EUROCONTROL and civil aviation authority requirements." },
    ],
    capabilities: [
      "Site survey, requirements capture and system design",
      "Supply, installation and commissioning (FAT/SAT)",
      "Systems integration with existing ATM ecosystems",
      "Operator and technician training programs",
      "Warranty, post-warranty and lifecycle support",
      "Documentation, safety cases and handover packages",
    ],
    partners: ["ATM system suppliers", "Voice communications vendors", "Surveillance sensor providers", "Simulator manufacturers"],
    useCases: [
      "Modernization of area and approach control centers",
      "Airport tower systems refresh and expansion",
      "Voice communication system replacements",
      "New ATC training academies and simulators",
      "Regional ANSP capacity and safety programs",
    ],
  },
];

export const findService = (slug) => services.find((s) => s.slug === slug);
